import { errorMessage, err, ok, parseJson, type Result  } from "@lancer-vtt/shared";

// ── Web Storage on the `Result` channel ───────────────────────────────────────
//
// `localStorage`/`sessionStorage` throw for reasons that have nothing to do with the
// caller: private-browsing modes deny the property outright, sandboxed iframes make
// even *reading* `window.localStorage` a `SecurityError`, quota-exceeded kills a write,
// and a non-browser realm (SSR, a bun test file with no DOM) has no such global at all.
// Every one of those is an *expected* rejection, which per `CODE_STYLE.md` belongs on
// `Result` — and per this module's reason for existing, the `try` that catches it lives
// here ONCE rather than at each of the ~30 call sites that used to hand-roll it.
//
// This stays in `frontend/` rather than `shared/`: web storage is browser-only, and
// `shared/` is imported by the backend.
//
// Typical call site — swallow and fall back, with no `try` in sight:
//
//   const r = storageGet(KEY);
//   if (!r.ok || r.value === undefined) return DEFAULT;
//
// **Absent and unavailable are different answers.** A key that isn't set is
// `ok(undefined)`; a store that couldn't be reached or read is `err(...)`. Most call
// sites collapse the two into one fallback, but the ones that want to distinguish
// (e.g. "don't overwrite what we couldn't read") can.

/** Which Web Storage area to talk to. `local` persists across sessions; `session` doesn't. */
export type StorageArea = "local" | "session";

/** The message an unreachable store reports, when the throw carried nothing better. */
const UNAVAILABLE = "web storage unavailable";

/**
 * Run `read` against the requested store, converting every failure mode — absent global,
 * throwing property getter, throwing method — into `err`.
 *
 * This is the module's single `try`. Everything public routes through it.
 */
function withStorage<T>(area: StorageArea, read: (store: Storage) => T): Result<T> {
  try {
    // A property read, not a bare identifier: in a realm without web storage this is
    // `undefined` rather than a `ReferenceError`, and where the getter itself throws
    // (sandboxed iframe) the `catch` below takes it.
    const store = area === "session" ? globalThis.sessionStorage : globalThis.localStorage;
    if (store === undefined || store === null) return err(UNAVAILABLE);
    return ok(read(store));
  } catch (thrown) {
    return err(errorMessage(thrown));
  }
}

/**
 * Read a raw string value. `ok(undefined)` means the key is unset; `err` means the store
 * itself couldn't be reached or read.
 */
export function storageGet(key: string, area: StorageArea = "local"): Result<string | undefined> {
  return withStorage(area, (store) => store.getItem(key) ?? undefined);
}

/** Write a raw string value. `err` on an unreachable store or an exceeded quota. */
export function storageSet(key: string, value: string, area: StorageArea = "local"): Result<void> {
  return withStorage(area, (store) => store.setItem(key, value));
}

/** Delete a key. `err` only if the store is unreachable — removing an unset key is `ok`. */
export function storageRemove(key: string, area: StorageArea = "local"): Result<void> {
  return withStorage(area, (store) => store.removeItem(key));
}

/**
 * Read a key and JSON-parse it in one call — the composite most persisted client state
 * actually wants. `ok(undefined)` for an unset key; `err` for an unreachable store *or*
 * for a value that isn't valid JSON (a hand-edit, a half-written entry, a stale format).
 *
 * `T` is an unchecked assertion, exactly as `JSON.parse(raw) as T` was — a caller that
 * cares about the shape still validates the value it gets back.
 */
export function storageGetJson<T>(key: string, area: StorageArea = "local"): Result<T | undefined> {
  const raw = storageGet(key, area);
  if (!raw.ok) return err(raw.error);
  if (raw.value === undefined) return ok(undefined);
  return parseJson<T>(raw.value);
}

/**
 * JSON-serialize a value and write it in one call. `err` on an unreachable store, an
 * exceeded quota, *or* a value `JSON.stringify` refuses (a cycle, a throwing `toJSON`) —
 * which is why the serialize happens inside the guarded region rather than at the call site.
 */
export function storageSetJson(key: string, value: unknown, area: StorageArea = "local"): Result<void> {
  return withStorage(area, (store) => store.setItem(key, JSON.stringify(value)));
}
